import { useCallback, useEffect, useState } from 'react';
import { apiClient, axiosClient, useAuth } from '../../../auth-lib';
import { Card, EmptyState, Skeleton, useToast } from '../../../design-system';
import NoteComposer, { emptyDraft } from '../notes/NoteComposer';
import NoteList from '../notes/NoteList';
import '../../styles/ClientNotes.css';

// The firm's PRIVATE notes on ONE client.
//
// ⚠️ THIS CLIENT'S NOTES AND NO OTHERS, and that is the SERVER's doing, not
// this file's: `GET /api/client-notes?clientId=…` matches `clientId` in the
// query, so another client's note — and every general note, which belongs to no
// client — is never fetched here at all. There is deliberately no filtering
// step below to "make sure", because a filter here would suggest the boundary
// lives in the browser.
//
// ⚠️ NEVER CLIENT-VISIBLE. There is no portal route to this data and the notes
// live in their own collection, so nothing client-facing can carry one by
// accident. The banner at the top of the section says so on screen, because a
// person about to type "the director is under investigation" deserves to know
// who can read it BEFORE they type it, not after.
//
// The section only renders for somebody holding `client-notes.access`.
// Everything below is a convenience on top of the server's own checks —
// `canWrite` and `canEdit` come from the API, so the UI can never offer an
// action the server would refuse.

const KINDS = [
  { value: '', label: 'All' },
  { value: 'shared', label: 'Shared' },
  { value: 'personal', label: 'Personal' },
];

export default function ClientNotes({ clientId }) {
  const toast = useToast();
  const { user } = useAuth();
  const canAccess = !!user?.permissions?.includes('client-notes.access');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);
  const [kind, setKind] = useState('');

  const load = useCallback(() => {
    if (!clientId || !canAccess) { setLoading(false); return; }
    setLoading(true);
    apiClient
      .get('/api/client-notes', { params: { clientId, ...(kind ? { kind } : {}) } })
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load notes'))
      .finally(() => setLoading(false));
  }, [clientId, canAccess, kind]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!canAccess) return;
    axiosClient.get('/users').then((r) => setOrgUsers(r.data || [])).catch(() => setOrgUsers([]));
  }, [canAccess]);

  // The section is invisible without the permission. The server refuses anyway;
  // this stops the page advertising that private notes exist at all.
  if (!canAccess) return null;

  const notes = data?.notes || [];
  const canWrite = !!data?.canWrite;

  const submit = async () => {
    if (!draft.body.trim()) return;
    setSaving(true);
    try {
      // ⚠️ `clientId` LAST — the draft carries its own (empty) one from the
      // shared composer, and spreading it after would file the note as general.
      await apiClient.post('/api/client-notes', { ...draft, clientId });
      setDraft(emptyDraft);
      toast.success('Note added');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save the note');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft({
      body: note.body,
      visibility: note.visibility,
      taggedUserIds: note.tagged.map((t) => t.id),
      isPinned: note.isPinned,
      clientId: note.clientId || '',
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      // The composer here offers no client picker, so `editDraft.clientId` is
      // whatever the note already had — the PATCH leaves the link alone.
      await apiClient.patch(`/api/client-notes/${id}`, editDraft);
      setEditingId(null);
      toast.success('Note updated');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not update the note');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      await apiClient.patch(`/api/client-notes/${note.id}`, { isPinned: !note.isPinned });
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not pin the note');
    }
  };

  const remove = async (note) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this note? It stays in the audit trail but disappears from here.')) return;
    try {
      await apiClient.delete(`/api/client-notes/${note.id}`);
      toast.success('Note deleted');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not delete the note');
    }
  };

  return (
    <Card>
      <div className="cn-head">
        <div>
          <h3 className="cn-title">Notes for this client</h3>
          <p className="cn-sub">
            Only notes filed against this client. Internal — the client never sees these, and
            there is no route to them from the portal.
          </p>
        </div>
        <span className="cn-badge">🔒 Firm only</span>
      </div>

      {loading ? (
        <Skeleton.Text lines={4} />
      ) : error ? (
        <p className="cn-error" role="alert">{error}</p>
      ) : (
        <>
          {canWrite ? (
            <NoteComposer
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              submitLabel="Add note"
              saving={saving}
              orgUsers={orgUsers}
              // No client picker: the client is the page you are standing on.
              placeholder="What should the team know about this client? Nobody outside the firm can read this."
            />
          ) : (
            <p className="cn-muted cn-readonly">
              You can read these notes but not add to them.
            </p>
          )}

          {/* Shared vs personal. The counts come from the server and describe
              everything THIS reader can see — so "Personal" never hints at a
              colleague's private notes. */}
          <div className="cn-kinds" role="tablist" aria-label="Note kind">
            {KINDS.map((k) => {
              const n = k.value ? data?.counts?.[k.value] : (data?.counts?.shared || 0) + (data?.counts?.personal || 0);
              return (
                <button
                  type="button"
                  key={k.value || 'all'}
                  role="tab"
                  aria-selected={kind === k.value}
                  className={`cn-kind ${kind === k.value ? 'is-on' : ''}`}
                  onClick={() => setKind(k.value)}
                >
                  {k.label}<span className="cn-kind-n">{n ?? 0}</span>
                </button>
              );
            })}
          </div>

          {notes.length === 0 ? (
            <EmptyState
              title={kind === 'personal' ? 'No personal notes yet' : 'No notes yet'}
              description={kind === 'personal'
                ? 'Your own thinking on this client. Only you (and anyone you tag) can read it.'
                : 'Record what the team should know before acting on this client.'}
            />
          ) : (
            <NoteList
              notes={notes}
              editingId={editingId}
              editDraft={editDraft}
              setEditDraft={setEditDraft}
              onStartEdit={startEdit}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={saveEdit}
              onTogglePin={togglePin}
              onDelete={remove}
              saving={saving}
              orgUsers={orgUsers}
            />
          )}
        </>
      )}
    </Card>
  );
}
